import store from "../../../../../../../../store";

/**
 * Mobile optimization utilities for 3D models
 */
class MobileOptimizer {
  constructor() {
    this.isMobile = store.getState().serverState.isMobile || /iPad|iPhone/.test(navigator.userAgent);
    this.textureSize = 512; // Reasonable compression
    this.debugLogging = true;
  }

  /**
   * Check if optimizations should be applied
   * @returns {boolean}
   */
  shouldOptimize() {
    return this.isMobile;
  }

  /**
   * Optimize model for mobile devices
   * @param {THREE.Object3D} model
   */
  async optimizeModel(model) {
    if (!this.shouldOptimize()) return;

    const textureMap = new Map(); // Reuse compressed textures

    model.traverse(async (child) => {
      if (child.isMesh) {
        // Optimize materials and textures
        if (child.material) {
          await this.optimizeMaterial(child.material, textureMap);
        }

        // Disable shadows for performance
        child.castShadow = false;
        child.receiveShadow = false;
        child.frustumCulled = true; // Enable frustum culling
      }
    });

    if (this.debugLogging) {
      console.log('Applied mobile optimizations to model');
    }
  }

  /**
   * Optimize material for mobile with texture compression
   * @param {THREE.Material} material
   * @param {Map} textureMap
   */
  async optimizeMaterial(material, textureMap) {
    if (!material) return;

    // Compress main texture if present
    if (material.map) {
      const compressedTexture = await this.compressTexture(material.map, textureMap);
      material.map = compressedTexture;
    }

    // Remove expensive maps to save memory
    material.normalMap = null;
    material.roughnessMap = null;
    material.metalnessMap = null;
    material.aoMap = null;
    material.displacementMap = null;
    material.emissiveMap = null;
    material.lightMap = null;
    material.bumpMap = null;
    material.alphaMap = null;
    material.envMap = null;

    // Simplify material properties for better performance
    if (material.metalness !== undefined) material.metalness = 0.1;
    if (material.roughness !== undefined) material.roughness = 0.8;

    material.needsUpdate = true;
  }

  /**
   * Compress texture for mobile devices
   * @param {THREE.Texture} texture
   * @param {Map} textureMap
   * @returns {THREE.Texture}
   */
  async compressTexture(texture, textureMap) {
    if (!texture || !texture.image) return texture;

    const textureKey = texture.uuid;
    if (textureMap.has(textureKey)) {
      return textureMap.get(textureKey);
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Reasonable size reduction
    const maxSize = this.textureSize;
    const img = texture.image;

    if (img.width <= maxSize && img.height <= maxSize) {
      // No need to compress if already small enough
      textureMap.set(textureKey, texture);
      return texture;
    }

    const scale = Math.min(maxSize / img.width, maxSize / img.height);
    canvas.width = Math.floor(img.width * scale);
    canvas.height = Math.floor(img.height * scale);

    try {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Create new texture from compressed canvas
      const compressedTexture = new window.THREE.CanvasTexture(canvas);
      compressedTexture.minFilter = window.THREE.LinearFilter;
      compressedTexture.magFilter = window.THREE.LinearFilter;
      compressedTexture.generateMipmaps = false;
      compressedTexture.flipY = texture.flipY;
      compressedTexture.wrapS = texture.wrapS;
      compressedTexture.wrapT = texture.wrapT;

      textureMap.set(textureKey, compressedTexture);

      if (this.debugLogging) {
        console.log(`Compressed texture from ${img.width}x${img.height} to ${canvas.width}x${canvas.height}`);
      }

      return compressedTexture;
    } catch (error) {
      console.warn('Failed to compress texture:', error);
      textureMap.set(textureKey, texture);
      return texture;
    }
  }

  /**
   * Optimize instance for mobile rendering
   * @param {THREE.Object3D} instance
   */
  optimizeInstance(instance) {
    if (!this.shouldOptimize()) return;

    // Set low render order for appliances
    instance.renderOrder = -1;

    // Disable automatic matrix updates for static objects
    instance.matrixAutoUpdate = false;
    instance.updateMatrix();

    // Mark as optimized
    instance.userData.optimizedForMobile = true;

    instance.traverse(child => {
      if (child.isMesh) {
        // Disable auto matrix updates for meshes too
        child.matrixAutoUpdate = false;
        child.updateMatrix();

        // Set geometry to static draw
        if (child.geometry) {
          child.geometry.dynamic = false;
        }
      }
    });

    if (this.debugLogging) {
      console.log('Applied mobile instance optimizations');
    }
  }

  /**
   * Get mobile-optimized URL
   * @param {string} url
   * @returns {string}
   */
  getMobileOptimizedUrl(url) {
    if (!this.shouldOptimize()) return url;

    // Try different mobile URL patterns
    if (url.includes('.gltf')) {
      return url.replace('.gltf', '_mobile.gltf');
    }
    if (url.includes('/appliances/')) {
      return url.replace('/appliances/', '/appliances/mobile/');
    }
    return url + '?mobile=true'; // Query parameter approach
  }

  /**
   * Trigger garbage collection if available
   */
  triggerGarbageCollection() {
    if (this.shouldOptimize() && window.gc) {
      setTimeout(() => {
        window.gc();
        if (this.debugLogging) {
          console.log('Triggered garbage collection');
        }
      }, 100);
    }
  }

  /**
   * Get optimization settings
   * @returns {Object}
   */
  getSettings() {
    return {
      isMobile: this.isMobile,
      textureSize: this.textureSize,
      optimizationsEnabled: this.shouldOptimize()
    };
  }
}

export default MobileOptimizer;
